import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft,
  Edit,
  Archive,
  RotateCcw,
  Loader2,
  AlertTriangle,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { projectsApi } from '../api';
import { ProjectFormDialog } from '../components/ProjectFormDialog';
import { ProjectTeamTab } from '../components/ProjectTeamTab';
import { ProjectExpensesTab } from '../components/ProjectExpensesTab';
import { ProjectLaborTab } from '../components/ProjectLaborTab';
import { ProjectSubcontractorsTab } from '../components/ProjectSubcontractorsTab';
import { ProjectFinancialsTab } from '../components/ProjectFinancialsTab';
import { ProjectQuoteTab } from '../components/ProjectQuoteTab';
import { ProjectPhotosTab } from '../components/ProjectPhotosTab';
import { ProjectDocumentsTab } from '../components/ProjectDocumentsTab';
import { ProjectStatusBadge } from '../components/ProjectStatusBadge';
import {
  statusLabel,
  typeLabel,
  fullAddress,
  fmtDate,
  centsToDollarString,
} from '../constants';

const InfoRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 py-2 border-b border-gray-800/60 last:border-0">
    <span className="text-gray-500 text-sm">{label}</span>
    <span className="text-gray-200 text-sm sm:text-right">
      {value || <span className="text-gray-600 italic">Not added yet</span>}
    </span>
  </div>
);

const OverviewCard = ({ title, children }) => (
  <Card className="bg-[#111111] border-gray-800 p-5">
    <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide mb-3">{title}</h3>
    {children}
  </Card>
);

export const OpsProjectWorkspace = () => {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | notfound | error
  const [editOpen, setEditOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const res = await projectsApi.get(projectId);
      setProject(res.data);
      setState('ready');
    } catch (e) {
      setState(e.response?.status === 404 ? 'notfound' : 'error');
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleArchive = async () => {
    try {
      await projectsApi.archive(projectId);
      toast.success('Project archived');
      setConfirmArchive(false);
      load();
    } catch {
      toast.error('Failed to archive project');
    }
  };

  const handleRestore = async () => {
    try {
      await projectsApi.restore(projectId);
      toast.success('Project restored');
      load();
    } catch {
      toast.error('Failed to restore project');
    }
  };

  const handleComplete = async () => {
    try {
      const res = await projectsApi.update(projectId, { status: 'completed' });
      toast.success('Project completed — it now counts toward company Financials');
      setConfirmComplete(false);
      setProject(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not complete project');
    }
  };

  const handleDelete = async () => {
    try {
      await projectsApi.delete(projectId);
      toast.success('Project deleted');
      navigate('/ops/projects');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not delete project');
    }
  };

  if (state === 'loading') {
    return (
      <div className="text-center py-24 text-gray-500" data-testid="workspace-loading">
        <Loader2 size={28} className="mx-auto mb-3 animate-spin text-[#D4AF37]" />
        Loading project…
      </div>
    );
  }

  if (state === 'notfound' || state === 'error') {
    return (
      <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="workspace-notfound">
        <AlertTriangle size={36} className="mx-auto mb-4 text-gray-600" />
        <h3 className="text-xl font-semibold text-white mb-2">
          {state === 'notfound' ? 'Project not found' : 'Something went wrong'}
        </h3>
        <p className="text-gray-500 mb-6">
          {state === 'notfound'
            ? 'This project may have been removed or the link is incorrect.'
            : 'We could not load this project. Please try again.'}
        </p>
        <Button
          onClick={() => navigate('/ops/projects')}
          className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
          data-testid="workspace-back-btn"
        >
          <ArrowLeft size={16} /> Back to Projects
        </Button>
      </Card>
    );
  }

  const p = project;

  return (
    <div data-testid="ops-project-workspace">
      <button
        onClick={() => navigate('/ops/projects')}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#D4AF37] mb-4"
        data-testid="workspace-back-link"
      >
        <ArrowLeft size={16} /> Projects
      </button>

      {p.is_archived && (
        <div
          className="flex items-center gap-2 bg-gray-800/50 border border-gray-700 text-gray-300 rounded-lg px-4 py-2 mb-4 text-sm"
          data-testid="workspace-archived-banner"
        >
          <Archive size={16} /> This project is archived. It is hidden from the active Projects list.
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl md:text-3xl font-bold text-white" data-testid="workspace-title">
              {p.name}
            </h2>
            <ProjectStatusBadge project={p} disabled={p.is_archived} onChanged={(updated) => setProject(updated)} />
            {p.is_financially_closed && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full border bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40" data-testid="workspace-financially-closed-badge">Financially Closed</span>
            )}
          </div>
          <p className="text-gray-400 mt-1">{typeLabel(p.project_type, p.custom_project_type)}</p>
          <div className="text-sm text-gray-500 mt-2 space-y-0.5">
            {fullAddress(p) && <div>{fullAddress(p)}</div>}
            {p.customer_name && <div>Customer: {p.customer_name}</div>}
            <div>
              {fmtDate(p.start_date) ? `Start: ${fmtDate(p.start_date)}` : 'Start: —'}
              {'  ·  '}
              {fmtDate(p.target_completion_date)
                ? `Target: ${fmtDate(p.target_completion_date)}`
                : 'Target: —'}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setEditOpen(true)}
            variant="outline"
            className="border-gray-700 text-gray-300 hover:text-[#D4AF37] hover:border-[#D4AF37]"
            data-testid="workspace-edit-btn"
          >
            <Edit size={16} /> Edit
          </Button>
          {p.is_archived ? (
            <Button
              onClick={handleRestore}
              className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
              data-testid="workspace-restore-btn"
            >
              <RotateCcw size={16} /> Restore
            </Button>
          ) : (
            <Button
              onClick={() => setConfirmArchive(true)}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:text-red-400 hover:border-red-500"
              data-testid="workspace-archive-btn"
            >
              <Archive size={16} /> Archive
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setSearchParams(v === 'overview' ? {} : { tab: v })}
        className="w-full"
      >
        <TabsList className="bg-[#111111] border border-gray-800 flex flex-wrap h-auto justify-start gap-1 p-1">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            data-testid="workspace-tab-overview"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="team"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            data-testid="workspace-tab-team"
          >
            Team
          </TabsTrigger>
          <TabsTrigger
            value="financials"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            data-testid="workspace-tab-financials"
          >
            Financials
          </TabsTrigger>
          <TabsTrigger
            value="expenses-receipts"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            data-testid="workspace-tab-expenses-receipts"
          >
            Expenses &amp; Receipts
          </TabsTrigger>
          <TabsTrigger
            value="labor"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            data-testid="workspace-tab-labor"
          >
            Labor
          </TabsTrigger>
          <TabsTrigger
            value="subcontractors"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            data-testid="workspace-tab-subcontractors"
          >
            Subcontractors
          </TabsTrigger>
          <TabsTrigger
            value="quote-change-orders"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            data-testid="workspace-tab-quote-change-orders"
          >
            Quote &amp; Change Orders
          </TabsTrigger>
          <TabsTrigger
            value="photos"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            data-testid="workspace-tab-photos"
          >
            Photos
          </TabsTrigger>
          <TabsTrigger
            value="documents"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
            data-testid="workspace-tab-documents"
          >
            Documents
          </TabsTrigger>
        </TabsList>

        {/* Overview (functional) */}
        <TabsContent value="overview" className="mt-6" data-testid="workspace-overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <OverviewCard title="Project Summary">
              <InfoRow label="Status" value={statusLabel(p.status)} />
              <InfoRow label="Project Type" value={typeLabel(p.project_type, p.custom_project_type)} />
              <InfoRow label="Project Manager" value={p.project_manager} />
              <InfoRow label="Description" value={p.description} />
              {p.source_quote_id && (
                <div className="flex justify-between gap-3 text-sm py-1" data-testid="overview-source-quote">
                  <span className="text-gray-500">Created from Quote</span>
                  <button
                    onClick={() => navigate(`/ops/quotes/${p.source_quote_id}`)}
                    className="text-[#D4AF37] hover:underline text-right"
                    data-testid="overview-open-source-quote"
                  >
                    Open Original Quote
                  </button>
                </div>
              )}
            </OverviewCard>

            <OverviewCard title="Customer Information">
              <InfoRow label="Name" value={p.customer_name} />
              <InfoRow label="Phone" value={p.customer_phone} />
              <InfoRow label="Email" value={p.customer_email} />
            </OverviewCard>

            <OverviewCard title="Property Information">
              <InfoRow label="Address" value={fullAddress(p)} />
            </OverviewCard>

            <OverviewCard title="Schedule">
              <InfoRow label="Start Date" value={fmtDate(p.start_date)} />
              <InfoRow label="Target Completion" value={fmtDate(p.target_completion_date)} />
              {p.actual_completion_date && (
                <InfoRow label="Actual Completion" value={fmtDate(p.actual_completion_date)} />
              )}
            </OverviewCard>

            <OverviewCard title="Financial Planning">
              <InfoRow label="Customer Budget" value={centsToDollarString(p.customer_budget_cents)} />
              <InfoRow label="Current Contract Value" value={centsToDollarString(p.current_contract_value_cents)} />
              {p.approved_change_order_count > 0 && (
                <p className="text-xs text-gray-500" data-testid="workspace-co-contract-note">
                  Base {centsToDollarString(p.base_contract_value_cents)} adjusted by {p.approved_change_order_count} approved change order{p.approved_change_order_count === 1 ? '' : 's'}.
                </p>
              )}
              <InfoRow
                label="Internal Cost Budget"
                value={centsToDollarString(p.internal_cost_budget_cents)}
              />
              <p className="text-xs text-gray-500 mt-3">
                Live cost tracking and projected profit are available in the Financials tab.
              </p>
            </OverviewCard>

            <OverviewCard title="Internal Notes">
              {p.internal_notes ? (
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{p.internal_notes}</p>
              ) : (
                <p className="text-sm text-gray-600 italic">Not added yet</p>
              )}
              <p className="text-xs text-gray-600 mt-3">Internal only — not shown to customers.</p>
            </OverviewCard>
          </div>

          {/* Complete / Delete Project */}
          <div className="mt-8 flex flex-col items-center gap-4">
            {p.status === 'completed' ? (
              <p className="text-sm text-green-400 text-center" data-testid="project-completed-note">
                This project is completed and is included in company Financials.
              </p>
            ) : !p.is_archived ? (
              <Button
                onClick={() => setConfirmComplete(true)}
                className="bg-red-600 text-white hover:bg-red-700 font-semibold px-8 py-6"
                data-testid="complete-project-btn"
              >
                Complete Project
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              className="border-red-900 text-red-400 hover:text-red-300 hover:border-red-700"
              data-testid="delete-project-btn"
            >
              <Trash2 size={15} /> Delete Project
            </Button>
          </div>
        </TabsContent>

        {/* Team (functional) */}
        <TabsContent value="team" className="mt-6" data-testid="workspace-team">
          <ProjectTeamTab project={p} />
        </TabsContent>

        {/* Expenses & Receipts (functional) */}
        <TabsContent value="expenses-receipts" className="mt-6" data-testid="workspace-expenses-receipts">
          <ProjectExpensesTab project={p} />
        </TabsContent>

        {/* Labor (functional) */}
        <TabsContent value="labor" className="mt-6" data-testid="workspace-labor">
          <ProjectLaborTab project={p} />
        </TabsContent>

        {/* Subcontractors (functional) */}
        <TabsContent value="subcontractors" className="mt-6" data-testid="workspace-subcontractors">
          <ProjectSubcontractorsTab project={p} />
        </TabsContent>

        {/* Financials (functional) */}
        <TabsContent value="financials" className="mt-6" data-testid="workspace-financials">
          <ProjectFinancialsTab project={p} />
        </TabsContent>

        {/* Quote & Change Orders (functional) */}
        <TabsContent value="quote-change-orders" className="mt-6" data-testid="workspace-quote-change-orders">
          <ProjectQuoteTab project={p} />
        </TabsContent>

        {/* Photos & Documents (functional) */}
        <TabsContent value="photos" className="mt-6" data-testid="workspace-photos">
          <ProjectPhotosTab project={p} />
        </TabsContent>
        <TabsContent value="documents" className="mt-6" data-testid="workspace-documents">
          <ProjectDocumentsTab project={p} />
        </TabsContent>
      </Tabs>

      <ProjectFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        project={p}
        onSaved={(updated) => setProject(updated)}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="delete-project-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{p.name}" permanently?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This deletes the project and EVERYTHING inside it: expenses, receipts, photos, documents,
              labor entries, subcontractor costs, change orders, and appointments. Its numbers are removed
              from company Financials. It will be kept in Recently Deleted for 30 days, where you can
              restore it — after that it's gone forever.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="delete-project-confirm-btn"
            >
              Delete Everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmComplete} onOpenChange={setConfirmComplete}>
        <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="complete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Complete this project?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              "{p.name}" will move to Completed Projects and its results (budget, costs, and profit) will be
              included in company Financials. You can change the status back later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleComplete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="complete-confirm-btn"
            >
              Complete Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this project?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              The project will be removed from the normal active Projects list but its information
              will be preserved. You can restore it at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="archive-confirm-btn"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
